import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is an npm package, not a local convex/ directory file
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ require three levels up to reach convex/_generated/
import { useOrgContext } from '@/hooks/useOrgContext';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableColumnHeader } from '@/components/insurance/SortableColumnHeader';
import { ColumnSettingsPopover } from '@/components/insurance/ColumnSettingsPopover';
import { PREAUTH_COLUMNS } from '@/lib/tableColumns';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FileText } from 'lucide-react';
import type { IPreAuthorization, TPreAuthStatus } from '@/@types/claims';
import type { IColumnConfig } from '@/@types/tableColumnVisibility';
import { cn } from '@/lib/utils';

const STATUS_BADGE: Record<TPreAuthStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  requested: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  under_review: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  denied: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  expired: 'bg-muted text-muted-foreground',
};

const STATUS_LABELS: Record<TPreAuthStatus, string> = {
  draft: 'Draft',
  requested: 'Requested',
  under_review: 'Under Review',
  approved: 'Approved',
  denied: 'Denied',
  expired: 'Expired',
};

function formatCurrency(amount: number | undefined, currency: string | undefined) {
  if (amount === undefined || !currency) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(ts: number) {
  return new Date(ts).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function PreAuthStatusBadge({ status }: { status: TPreAuthStatus }) {
  return (
    <Badge className={cn('text-xs font-medium', STATUS_BADGE[status])}>
      {STATUS_LABELS[status]}
    </Badge>
  );
}

function TableSkeleton({ colCount }: { colCount: number }) {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <TableRow key={i}>
          {Array.from({ length: colCount }).map((__, j) => (
            <TableCell key={j}>
              <Skeleton className="h-4 w-24" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function renderPreAuthCell(colId: string, pa: IPreAuthorization): React.ReactNode {
  switch (colId) {
    case 'paNumber':
      return <span className="font-medium">{pa.preAuthNumber}</span>;
    case 'status':
      return <PreAuthStatusBadge status={pa.status} />;
    case 'treatmentType':
      return <span className="text-muted-foreground">{pa.treatmentType ?? '—'}</span>;
    case 'proposedTreatment':
      return (
        <span className="text-muted-foreground max-w-[200px] truncate block">
          {pa.proposedTreatment ?? '—'}
        </span>
      );
    case 'provider':
      return <span className="text-muted-foreground">{pa.proposedProvider ?? '—'}</span>;
    case 'estimatedCost':
      return (
        <span className="text-muted-foreground">
          {formatCurrency(pa.estimatedCost, pa.currency)}
        </span>
      );
    case 'submitted':
      return <span className="text-muted-foreground">{formatDate(pa.createdAt)}</span>;
    default:
      return null;
  }
}

function PreAuthTableRow({
  pa,
  visibleColumns,
  onClick,
}: {
  pa: IPreAuthorization;
  visibleColumns: IColumnConfig[];
  onClick: () => void;
}) {
  return (
    <TableRow className="cursor-pointer hover:bg-muted/50" onClick={onClick}>
      {visibleColumns.map(col => (
        <TableCell
          key={col.id}
          className={cn(col.pinned === 'left' && 'sticky left-0 z-10 bg-background')}
        >
          {renderPreAuthCell(col.id, pa)}
        </TableCell>
      ))}
    </TableRow>
  );
}

export function PreAuthListPage() {
  const navigate = useNavigate();
  const { currentOrg, isLoading: orgLoading } = useOrgContext();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const rawPreAuths = useQuery(
    api.insurance.preAuthorizations.list,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );

  const isLoading = orgLoading || rawPreAuths === undefined;
  const preAuths = useMemo(() => rawPreAuths ?? [], [rawPreAuths]);

  const { visibleColumns, allColumns, isColumnVisible, toggleColumn, resetToDefaults } =
    useColumnVisibility('insurance-preauth', PREAUTH_COLUMNS);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return preAuths.filter(pa => {
      if (statusFilter !== 'all' && pa.status !== statusFilter) return false;
      if (!q) return true;
      return (
        pa.preAuthNumber.toLowerCase().includes(q) ||
        pa.claimantId.toLowerCase().includes(q) ||
        (pa.treatmentType ?? '').toLowerCase().includes(q) ||
        (pa.proposedTreatment ?? '').toLowerCase().includes(q)
      );
    });
  }, [preAuths, search, statusFilter]);

  const preAuthAccessor = useCallback((pa: IPreAuthorization, colId: string): string | number => {
    switch (colId) {
      case 'paNumber':
        return pa.preAuthNumber;
      case 'status':
        return pa.status;
      case 'treatmentType':
        return pa.treatmentType ?? '';
      case 'proposedTreatment':
        return pa.proposedTreatment ?? '';
      case 'provider':
        return pa.proposedProvider ?? '';
      case 'estimatedCost':
        return pa.estimatedCost ?? 0;
      case 'submitted':
        return pa.createdAt;
      default:
        return '';
    }
  }, []);
  const {
    sortState,
    toggleSort,
    sortedItems: sortedPreAuths,
  } = useTableSort(
    filtered,
    PREAUTH_COLUMNS,
    { defaultSort: { sortBy: 'submitted', direction: 'desc' } },
    preAuthAccessor
  );

  const hasActiveFilters = search !== '' || statusFilter !== 'all';

  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <FileText className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to manage pre-authorizations.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Pre-Authorizations</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isLoading ? 'Loading...' : `${preAuths.length} total`}
          </p>
        </div>
      </div>

      <div className="bg-table-container border border-table-container-border rounded-none overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-b border-table-row-border">
          <label htmlFor="preauth-search" className="sr-only">
            Search pre-authorizations
          </label>
          <Input
            id="preauth-search"
            placeholder="Search by PA number or claimant..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="max-w-xs"
          />
          <label htmlFor="preauth-status-filter" className="sr-only">
            Filter by status
          </label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger id="preauth-status-filter" className="w-[180px]">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="requested">Requested</SelectItem>
              <SelectItem value="under_review">Under Review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="denied">Denied</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
          <div className="ml-auto">
            <ColumnSettingsPopover
              allColumns={allColumns}
              isColumnVisible={isColumnVisible}
              toggleColumn={toggleColumn}
              resetToDefaults={resetToDefaults}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table className="min-w-max">
            <TableHeader>
              <TableRow>
                {visibleColumns.map(col => (
                  <TableHead
                    key={col.id}
                    className={cn(col.pinned === 'left' && 'sticky left-0 z-10 bg-table-header')}
                  >
                    <SortableColumnHeader
                      column={col}
                      sortState={sortState}
                      onToggleSort={toggleSort}
                    />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <TableSkeleton colCount={visibleColumns.length} />}
              {!isLoading &&
                sortedPreAuths.map(pa => (
                  <PreAuthTableRow
                    key={pa._id}
                    pa={pa}
                    visibleColumns={visibleColumns}
                    onClick={() => navigate(`/insurance/pre-authorizations/${pa._id}`)}
                  />
                ))}
              {!isLoading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length}>
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <FileText className="w-12 h-12 text-muted-foreground mb-4" />
                      <h3 className="text-lg font-medium text-foreground mb-1">
                        No pre-authorizations found
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {hasActiveFilters
                          ? 'Try adjusting your filters or search query'
                          : 'No pre-authorizations have been submitted yet'}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
